import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { dynamicCollectionRendererKey } from '@epicuro-next/constants/dynamic-elements';
import { DataProvidersResolver } from '@epicuro-next/data-providers/providers-registry';
import {
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
  IDynamicItem,
  IDynamicItemStyles,
  IDynamicOutputs,
  isIDynamicBindableValueOptions,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { COMPONENTS_MAP } from '@epicuro-next/platform/dynamic-components-registrator';
import {
  ComponentsRelationshipService,
  ErrorCallbacks,
  ErrorContainer,
  resolveValues,
} from '@epicuro-next/platform/dynamic-value-resolving';
import { ensureArray } from '@epicuro-next/utilities';
import { Store } from '@ngrx/store';
import { IdProviderServiceFactoryService } from 'libs/platform/dynamic-value-resolving/src/lib/services/id-provider-service-factory.service';
import { IdProviderService } from 'libs/platform/dynamic-value-resolving/src/lib/services/id-provider.service';
import { isObservable, of, Subject, take, takeUntil } from 'rxjs';

import { CollectionsRendererComponent } from '../components/collections-renderer/collections-renderer.component';
import { DynamicScope } from '../interfaces/dynamic-scope';
import { IdContainer } from '../interfaces/id-container';
import {
  setComponentKeyToData,
  setDataOnComponent,
} from '../utils/set-data-on-component';
import { setInputsOnComponent } from '../utils/set-input-on-component';
import {
  getPayload,
  handleEvent,
  setupAllDifferentKindsOfEventHandlers,
  setupOutputEventHandlers,
} from '../utils/setup-event-handlers';

import { ScopeContainer } from './../interfaces/scope-container';
import { ComponentsAttributesService } from './components-attributes.service';
import { ComponentsStylesResolverService } from './components-styles-resolver.service';

export interface IComponentOptions {
  id?: string;
  isRoot?: boolean;
  scope?: DynamicScope;
  inputs?: Record<string, unknown>;
  eventHandlers?: IDynamicItem['eventHandlers'];
  styles?: Partial<IDynamicItemStyles>;
  children?: IDynamicItem[];
  data?: Record<string, unknown>;
  parent?: unknown;
  outputs?: IDynamicOutputs;
  attributes?: { [key: string]: string };
  events?: IDynamicItem['events'];
}

/** Components resolver service */
@Injectable()
export class ComponentsResolverService {
  constructor(
    private readonly componentsAttributesService: ComponentsAttributesService,
    private readonly componentsRelationshipService: ComponentsRelationshipService,
    private readonly componentsStylesResolverService: ComponentsStylesResolverService,
    private readonly dataProvidersResolver: DataProvidersResolver,
    private readonly idProviderServiceFactoryService: IdProviderServiceFactoryService,
    private readonly store: Store,
  ) {}

  /** Method to get registered component by id */
  public getComponentDef(key: string) {
    const component = COMPONENTS_MAP.get(key);

    if (!component) {
      throw new Error(`Component with key ${key} hasn't been registered`);
    }

    return component;
  }

  public resolveComponent(
    contentTypeId: string,
    vcRef: ViewContainerRef,
    options?: IComponentOptions,
  ) {
    return this.resolveComponentInternal(
      contentTypeId,
      vcRef,
      undefined,
      options,
    );
  }

  private resolveComponentInternal(
    contentTypeId: string,
    vcRef: ViewContainerRef,
    postCreateComponentsTreeActions: (() => void)[] | undefined,
    options?: IComponentOptions,
  ) {
    const isTopMostParent = postCreateComponentsTreeActions === undefined;
    postCreateComponentsTreeActions ??= [];

    if (contentTypeId === dynamicCollectionRendererKey && options) {
      CollectionsRendererComponent.ensureChildrenAreSetInItemsInput(options);
    }

    const scope =
      options?.scope ?? (options?.parent as ScopeContainer)?.__scope__;
    if (scope === undefined) {
      throw new Error('Scope should have been set right now.');
    }
    const isRoot = options?.isRoot ?? false;
    const idProviderService = this.idProviderServiceFactoryService.get(
      scope,
      isRoot,
    );

    const componentRef = this.createComponentInternal(
      contentTypeId,
      vcRef,
      idProviderService,
      scope,
      postCreateComponentsTreeActions,
      options,
    );

    if (isTopMostParent) {
      // Run in reverse order so the top most parent is handled first, then the inner childs, and childs of childs and so forth.
      [...postCreateComponentsTreeActions]
        .reverse()
        .forEach((action) => action());

      idProviderService.instantiate(isRoot);
    }

    if (options?.isRoot) {
      componentRef.onDestroy(() => {
        idProviderService.ngOnDestroy();
      });
    }

    if (options?.attributes) {
      this.componentsAttributesService.attachAttributes(
        componentRef,
        options.attributes,
        scope,
        isRoot,
      );
    }

    return componentRef;
  }

  public createComponentInternal(
    componentKey: string,
    vcRef: ViewContainerRef,
    idProviderService: IdProviderService,
    scope: DynamicScope,
    postCreateComponentsTreeActions?: (() => void)[],
    options?: IComponentOptions,
  ): ComponentRef<unknown> {
    postCreateComponentsTreeActions ??= [];
    let projectableNodes: Node[][] | undefined;

    const childComponentRefs: ComponentRef<unknown>[] = [];

    if (options?.children !== undefined && options.children.length > 0) {
      projectableNodes = this.createChildNodes(
        vcRef,
        options.children,
        postCreateComponentsTreeActions,
        childComponentRefs,
        options.scope,
      );
    }

    const componentRef: ComponentRef<unknown> = vcRef.createComponent(
      this.getComponentDef(componentKey),
      {
        injector: vcRef.injector,
        projectableNodes,
      },
    );

    (componentRef.instance as ScopeContainer).__scope__ = scope;

    const id = options?.id;
    if (id !== undefined) {
      idProviderService.registerComponent(componentRef, id);
      (componentRef.instance as IdContainer).__id__ = id;
    }

    this.trackComponentsRelationships(
      componentRef,
      options,
      childComponentRefs,
    );

    postCreateComponentsTreeActions.push(() => {
      // The reasons to have this __data__ on the instance of the components are:
      // 1. Any component should be able to get a context with additional data, without having to modify the code of the component therefore.
      // 2. Setting inputs using the Angular API only works if there is an actual @Input() with that name defined, so that was no option.
      // 3. It should never collide with the actual implementation of that component, hence the pre and postfix understores around `data`.
      //    If it would ever collide one may choose to move the relationship between data and the component instance to a new service.
      //    This new service would then need to consider the life cycle of the component and therefore should probably work with a ComponenRef.
      //    So then it can subscribe to the OnDestroy lifecycle hook. If __data__ on the instance itself works, that is easiest of course.

      setComponentKeyToData(componentKey, componentRef);

      const data = options?.data;
      if (data !== undefined) {
        const dataErrorCallbacks: ErrorCallbacks = {};

        this.setupErrorHandling(
          data,
          dataErrorCallbacks,
          componentRef,
          idProviderService,
        );

        setDataOnComponent(
          this.dataProvidersResolver,
          this.componentsRelationshipService,
          idProviderService,
          componentRef,
          data,
          dataErrorCallbacks,
        );
      }

      const inputs = options?.inputs;
      if (inputs) {
        setInputsOnComponent(
          this.dataProvidersResolver,
          this.componentsRelationshipService,
          idProviderService,
          componentRef,
          inputs,
        );
      }

      if (options?.eventHandlers) {
        setupAllDifferentKindsOfEventHandlers(
          options?.eventHandlers,
          this.dataProvidersResolver,
          this.componentsRelationshipService,
          idProviderService,
          componentRef,
          (action) => this.store.dispatch(action),
        );
      }

      if (options?.outputs) {
        setupOutputEventHandlers(
          options.outputs,
          this.dataProvidersResolver,
          this.componentsRelationshipService,
          idProviderService,
          componentRef,
          (action) => this.store.dispatch(action),
        );
      }

      if (options?.styles) {
        this.componentsStylesResolverService.attachStyles(
          componentRef,
          options.styles,
          this.dataProvidersResolver,
          this.componentsRelationshipService,
          idProviderService,
        );
      }

      if (options?.events) {
        if (options.scope === undefined) {
          throw new Error(
            `The dynamic item defined events but there is no scope to propagate it on. Was DynamicCollectionsOutlet used (which sets the scope for you)? Events: ${JSON.stringify(
              options.events,
            )}`,
          );
        }
        this.propagateEventsToScope(
          options,
          idProviderService,
          componentRef,
          options.scope,
        );
      }

      if (id !== undefined) {
        idProviderService.postComponentsHaveBeenSetupActions.push(() => {
          idProviderService.componentHasBeenSetup(componentRef, id);
        });
      }
    });

    return componentRef;
  }

  private propagateEventsToScope(
    options: IComponentOptions,
    idProviderService: IdProviderService,
    componentRef: ComponentRef<unknown>,
    scope: DynamicScope,
  ) {
    const events = ensureArray(options.events);
    const eventsToResolve: Record<
      string,
      IDynamicBindableValue | IDynamicBindableValueCombination
    > = {};
    for (let i = 0; i < events.length; i++) {
      const recordKey = i.toString().padStart(3, '0');
      eventsToResolve[recordKey] = events[i];
    }

    const resolvedValues = resolveValues(
      eventsToResolve,
      this.dataProvidersResolver,
      this.componentsRelationshipService,
      idProviderService,
      componentRef,
    );

    const isDestroyed$ = new Subject<void>();
    componentRef.onDestroy(() => {
      isDestroyed$.next();
    });

    for (const resolvedValue of Object.values(resolvedValues)) {
      const resolvedValue$ = isObservable(resolvedValue)
        ? resolvedValue
        : of(resolvedValue);
      resolvedValue$
        .pipe(takeUntil(isDestroyed$.pipe(take(1))))
        .subscribe((event: unknown) => {
          scope.event.next(event);
        });
    }
  }

  private setupErrorHandling(
    data: Record<string, unknown>,
    errorCallbacks: ErrorCallbacks,
    componentRef: ComponentRef<unknown>,
    idProviderService: IdProviderService,
  ) {
    for (const key of Object.keys(data)) {
      const dataProperty = data[key];
      if (
        isIDynamicBindableValueOptions(dataProperty) &&
        dataProperty.onError
      ) {
        const errorEventHandlers = ensureArray(dataProperty.onError);
        errorCallbacks[key] = (errorContainer) => {
          for (const errorEventHandler of errorEventHandlers) {
            if (errorEventHandler.payload$ === undefined) {
              // Self bind the error value. Which would be equivalent to pseudo config { provider: self, property: data[key] }
              const errorPayload: ErrorContainer =
                this.getErrrorPayload(errorContainer);
              errorEventHandler.payload$ = errorPayload;
            }

            const payload$ = getPayload(
              componentRef,
              this.dataProvidersResolver,
              this.componentsRelationshipService,
              idProviderService,
              errorEventHandler,
            );

            handleEvent(
              errorEventHandler,
              (action) => this.store.dispatch(action),
              payload$,
            );
          }
        };
      }
    }
  }

  private getErrrorPayload(errorContainer: ErrorContainer) {
    const error = errorContainer.__resolveValueError__;
    const errorPayloadValue = error instanceof Error ? error.message : error;
    const errorPayload: ErrorContainer = {
      __resolveValueError__: errorPayloadValue,
    };
    return errorPayload;
  }

  private trackComponentsRelationships(
    componentRef: ComponentRef<unknown>,
    options: IComponentOptions | undefined,
    childComponentRefs: ComponentRef<unknown>[],
  ) {
    this.componentsRelationshipService.registerComponent(componentRef);

    if (options?.parent) {
      const parent = options.parent as unknown;
      const parentComponentRef =
        this.componentsRelationshipService.getComponentRef(parent);
      if (parentComponentRef) {
        this.componentsRelationshipService.registerChildComponent(
          parentComponentRef,
          componentRef,
        );
      }
    }

    childComponentRefs.forEach((childComponentRef) => {
      this.componentsRelationshipService.registerChildComponent(
        componentRef,
        childComponentRef,
      );
    });
  }

  private createChildNodes(
    vcRef: ViewContainerRef,
    children: IDynamicItem[],
    postCreateComponentsTreeActions: (() => void)[],
    childComponentRefs: ComponentRef<unknown>[],
    scope?: DynamicScope,
  ): Node[][] {
    return [
      children.map((item) => {
        // as of now Angular team doesn't allow adding hostDirectives dynamically, so this workaround was born.
        // in order to get the benefit of 'if' config and content projection, we have to wrap child components(content projected) into this dynamic element
        // https://github.com/angular/angular/issues/47728
        const childComponentRef = this.resolveComponentInternal(
          'dynamic-collections-renderer',
          vcRef,
          postCreateComponentsTreeActions,
          {
            inputs: {
              items: [item],
            },
            styles: {
              classes: ['d-contents'],
            },
            scope,
          },
        );

        childComponentRefs.push(childComponentRef);

        return childComponentRef.location.nativeElement;
      }),
    ];
  }
}
